import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { GroupType } from '@/components/bundle/bundleTypes';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { type BundleCreateRequest, createBundle } from '@/hooks/useBundle';
import { apiErrorMessage } from '@/lib/api-client';

type SelectedFile = {
  id: string;
  name: string;
};

type CreateBundleDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterGuid: string;
  onSuccess: (bundleId: string, bundleTitle: string) => void;
  selectedFiles?: SelectedFile[];
};

export const CreateBundleDrawer = ({
  open,
  onOpenChange,
  matterGuid,
  onSuccess,
  selectedFiles,
}: CreateBundleDrawerProps) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [removeBlankPages, setRemoveBlankPages] = useState(false);
  const [pageNumbersEnabled, setPageNumbersEnabled] = useState(true);
  const [batesStampEnabled, setBatesStampEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setNewTitle('');
      setNewDescription('');
      setRemoveBlankPages(false);
      setPageNumbersEnabled(true);
      setBatesStampEnabled(false);
      setIsSubmitting(false);
    }
  }, [open]);

  const groups = useMemo(() => {
    if (!selectedFiles?.length) return [];

    return [{
      bundle_group_id: null,
      title: 'Root',
      order_index: 0,
      group_type: GroupType.ROOT,
      files: selectedFiles.map((file, index) => ({
        bundle_file_id: null,
        file_reference_guid: file.id,
        order_index: index,
      })),
      subgroups: [],
    }];
  }, [selectedFiles]);

  const handleSubmit = async () => {
    if (!newTitle.trim()) return;
    setIsSubmitting(true);

    try {
      const request: BundleCreateRequest = {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        metadata: {
          remove_blank_pages: removeBlankPages,
          page_numbers: { enabled: pageNumbersEnabled },
          bates_stamp: { enabled: batesStampEnabled },
        },
        groups,
      };

      const result = await createBundle(matterGuid, request);
      onOpenChange(false);
      onSuccess(result.bundle_id, result.title);
    } catch (err: any) {
      toast.error(apiErrorMessage(err) || ui_labels.bundle_create_failed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{ui_labels.bundle_create_title}</SheetTitle>
          <SheetDescription>
            {ui_labels.bundle_create_hint}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex flex-col gap-4">
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={ui_labels.bundle_title_placeholder}
            autoFocus
          />
          <Textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder={ui_labels.bundle_description_placeholder}
            rows={4}
            className="resize-none"
          />

          {selectedFiles && selectedFiles.length > 0 && (
            <div className="rounded-md border p-3">
              <div className="mb-2 text-sm font-semibold">
                Selected files ({selectedFiles.length})
              </div>
              <div className="max-h-32 space-y-1 overflow-y-auto">
                {selectedFiles.map(file => (
                  <div key={file.id} className="truncate text-sm text-gray-700" title={file.name}>
                    {file.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2">
            <h3 className="mb-1 text-base font-semibold text-gray-900">Settings</h3>
            <p className="mb-4 text-sm text-gray-600">
              {ui_labels.bundle_settings_hint}
            </p>
            <div className="space-y-5 pt-2">
              <label className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Remove blank pages</span>
                <Checkbox checked={removeBlankPages} onCheckedChange={(checked) => setRemoveBlankPages(checked === true)} />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Page Numbers</span>
                <Checkbox checked={pageNumbersEnabled} onCheckedChange={(checked) => setPageNumbersEnabled(checked === true)} />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Bates Stamp</span>
                <Checkbox checked={batesStampEnabled} onCheckedChange={(checked) => setBatesStampEnabled(checked === true)} />
              </label>
            </div>
          </div>
        </div>
        <div className="mt-auto flex justify-end gap-2 pt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} text="Cancel" />
          <Button
            onClick={handleSubmit}
            disabled={!newTitle.trim() || isSubmitting}
            loading={isSubmitting}
            loadingText="Creating..."
            text="Create"
            size="lg"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
